import type { TimeSlot } from '@frontend/api-types';
import { P, dayjs, identity, match } from '@frontend/duck-tape';
import { useEffect, useState } from '@frontend/react';
import { useCreateDraftTask } from '../context/CreateDraftTaskContext';
import type { DraftInformationGatheringStep, DraftTaskStepChangeType } from '../types';

type UseInformationGatheringStepProps = {
  decrementParentStep: () => void;
  incrementParentStep: () => void;
};

// Helper function to create an ASAP time slot
const createAsapTimeSlot = (timezone?: string): TimeSlot => {
  const now = dayjs();
  const oneHourLater = now.add(1, 'hour');

  return {
    date: now.format('YYYY-MM-DD') as DateISOString,
    end: oneHourLater.toISOString() as DateTimeISOString,
    isRecommended: true,
    recommendationReason: 'ASAP scheduling',
    slotTzAbbr: timezone ? (timezone as IanaTimezone) : null,
    start: now.toISOString() as DateTimeISOString,
    startsNow: true,
    targetTzAbbr: timezone ? (timezone as IanaTimezone) : null,
    window: 'Morning',
  };
};

export const useInformationGatheringStep = ({
  decrementParentStep,
  incrementParentStep,
}: UseInformationGatheringStepProps) => {
  const { setState, state } = useCreateDraftTask();
  const [currentStep, setCurrentStep] = useState<DraftInformationGatheringStep>();

  const goToDescriptionStep = decrementParentStep;

  // Helper function to set ASAP time slot and proceed to next step
  const setAsapTimeSlotAndProceed = () => {
    const asapTimeSlot = createAsapTimeSlot(state.clientAddressTimezone);
    setState({ selectedTimeSlot: asapTimeSlot });
    incrementParentStep();
  };

  const changeInformationGatheringStep = (type: DraftTaskStepChangeType) => {
    if (!currentStep) return;
    match(type)
      .with('increment', () =>
        match(currentStep)
          .with('initial_questions', () => setAsapTimeSlotAndProceed())
          .exhaustive(),
      )
      .with('decrement', () =>
        match(currentStep)
          .with('initial_questions', () => decrementParentStep())
          .exhaustive(),
      )
      .exhaustive();
  };

  useEffect(() => {
    match(state.draftTaskEntity?.reviewStatus)
      .with('prelim_review', () => setCurrentStep('initial_questions'))
      .with('questions_ready', () => setCurrentStep('initial_questions'))
      // When the draft task is in final review, the user has already provided the final IQ.
      // We'll move them to the last IQ and show a loading indicator that disappears when the draft task
      // is either ready for scheduling or has failed final review
      .with('final_review', () => setCurrentStep('initial_questions'))
      .with('final_review_failed', () => decrementParentStep())
      .with('prelim_review_failed', () => decrementParentStep())
      .with('ready_for_scheduling', () => setAsapTimeSlotAndProceed())
      .with('draft_submitted', () => identity())
      .with('task_created', () => identity())
      .with(P.nullish, () => identity())
      .exhaustive();
  }, []);

  return {
    changeInformationGatheringStep,
    currentStep,
    goToDescriptionStep,
  };
};
